import React, { useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Mail, CheckCircle, Shield } from 'lucide-react';

const API = '/api';

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/newsletter`, { email });
      if (response.data.message.includes('check your email')) {
        toast.success('Please check your email to confirm your subscription!', { duration: 5000 });
        setIsSubscribed(true);
      } else if (response.data.message.includes('already subscribed')) {
        toast.info("You're already subscribed to our newsletter!");
      } else {
        toast.success(response.data.message);
        setIsSubscribed(true);
      }
      setEmail('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to subscribe. Please try again.';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const perks = [
    'New business listings & local deals',
    'Upcoming events across Essex',
    'Community news & exclusive offers',
  ];

  return (
    <section className="py-16 bg-[#0F2C3E] text-white relative overflow-hidden" data-testid="newsletter-section">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-16">

          {/* Left — Value Proposition */}
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-sm text-white/80 mb-4">
              <Mail className="h-3.5 w-3.5" />
              Free weekly newsletter
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
              Stay in the Loop
            </h2>
            <p className="text-white/70 mb-5 leading-relaxed">
              Join Essex residents who get the latest listings, events and community updates delivered every week.
            </p>
            <ul className="space-y-2.5">
              {perks.map((perk) => (
                <li key={perk} className="flex items-center gap-2.5 text-sm text-white/80">
                  <CheckCircle className="h-4 w-4 text-[#4A6741] flex-shrink-0" />
                  {perk}
                </li>
              ))}
            </ul>
          </div>

          {/* Right — Form */}
          <div className="w-full md:w-auto md:min-w-[360px]">
            <div className="bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              {isSubscribed ? (
                <div className="text-center py-4" data-testid="newsletter-success">
                  <CheckCircle className="h-10 w-10 text-[#4A6741] mx-auto mb-3" />
                  <p className="font-semibold text-lg">You're in!</p>
                  <p className="text-white/70 text-sm mt-1">Check your inbox to confirm your subscription.</p>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSignup} className="space-y-3" data-testid="newsletter-form">
                  <label htmlFor="newsletter-email-footer" className="block text-sm font-medium text-white/90 mb-1">
                    Your email address
                  </label>
                  <Input
                    id="newsletter-email-footer"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-white/10 border-white/15 text-white placeholder:text-white/40 h-12 w-full focus:border-[#4A6741] focus:ring-[#4A6741]/30"
                    data-testid="newsletter-input"
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#4A6741] hover:bg-[#3d5635] text-white h-12 font-semibold text-base"
                    data-testid="newsletter-btn"
                  >
                    {isSubmitting ? 'Subscribing...' : 'Subscribe — It\'s Free'}
                  </Button>
                  <p className="flex items-center justify-center gap-1.5 text-xs text-white/50 pt-1">
                    <Shield className="h-3 w-3" />
                    No spam, ever. Unsubscribe in one click.
                  </p>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;
